import { EntityType } from '@frontend/common';
import { useBlobRepository } from '@frontend/repository';
import type { BlobInfo } from '@frontend/repository';
import { useEffect, useState } from 'react';

export type AccountLogoStatus = 'idle' | 'loading' | 'loaded' | 'not_found' | 'error';

interface AccountLogoState {
    src: string | null;
    status: AccountLogoStatus;
}

const useAccountLogo = (accountId: string): AccountLogoState => {
    const blobRepository = useBlobRepository();
    const cached = blobRepository.getBlobStale(accountId, EntityType.ACCOUNT, accountId);
    const blobMissing = blobRepository.isBlobNotFound(accountId, EntityType.ACCOUNT, accountId);
    const [state, setState] = useState<AccountLogoState>(cached ? { src: cached.url, status: 'loaded' } : { src: null, status: 'idle' });

    useEffect(() => {
        let cancelled = false;

        if (cached) {
            setState({ src: cached.url, status: 'loaded' });
            return;
        }

        setState({ src: null, status: 'loading' });
        blobRepository
            .getBlob(accountId, EntityType.ACCOUNT, accountId)
            .then((blobInfo: BlobInfo | null) => {
                if (!cancelled) setState(blobInfo ? { src: blobInfo.url, status: 'loaded' } : { src: null, status: 'not_found' });
            })
            .catch(() => {
                if (!cancelled) setState({ src: null, status: 'error' });
            });

        return () => {
            cancelled = true;
        };
        // The repository functions are recreated by its provider. Re-running on those references would retry failed network requests indefinitely.
        // Cache changes still trigger this effect through `cached` and `blobMissing`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId, cached, blobMissing]);

    return state;
};

export default useAccountLogo;
